"use client";

import { useState, useRef, useEffect } from "react";
import { formatDateIndian } from "@/utils/date";

import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

interface DatePickerProps {
    value: string;
    onChange: (isoDate: string) => void;
}

export default function DatePicker({ value, onChange }: DatePickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    // Initialize current view month based on value or today
    const [currentMonth, setCurrentMonth] = useState(() => {
        return value ? new Date(value) : new Date();
    });
    const containerRef = useRef<HTMLDivElement>(null);

    // Helpers for Native Date Math
    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const days = [];
        // First day of current month
        const firstDay = new Date(year, month, 1);
        // Last day of current month
        const lastDay = new Date(year, month + 1, 0);

        // Days from previous month to fill the first row
        const startDayOfWeek = firstDay.getDay(); // 0 (Sun) to 6 (Sat)
        for (let i = startDayOfWeek; i > 0; i--) {
            const d = new Date(year, month, 1 - i);
            days.push({ date: d, isCurrentMonth: false });
        }

        // Days of current month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const d = new Date(year, month, i);
            days.push({ date: d, isCurrentMonth: true });
        }

        // Days from next month to fill the last row (optional, but good for grid)
        const totalSlots = Math.ceil(days.length / 7) * 7;
        const remainingSlots = totalSlots - days.length;
        for (let i = 1; i <= remainingSlots; i++) {
            const d = new Date(year, month + 1, i);
            days.push({ date: d, isCurrentMonth: false });
        }

        return days;
    };

    const days = getDaysInMonth(currentMonth);

    const prevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const handleDateClick = (date: Date) => {
        // Format to YYYY-MM-DD manually to avoid timezone issues
        const offsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
        const isoDate = offsetDate.toISOString().split('T')[0];
        onChange(isoDate);
        setIsOpen(false);
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Format helpers
    const formatMonthYear = (date: Date) => {
        return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    // Better selected check: compare ISO strings
    const checkIsSelected = (d: Date) => {
        const offsetDate = new Date(d.getTime() - (d.getTimezoneOffset() * 60000));
        const iso = offsetDate.toISOString().split('T')[0];
        return iso === value;
    }


    const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    return (
        <div className="relative" ref={containerRef}>
            {/* Trigger Button & Navigation */}
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl p-1 w-full relative">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!value) return;
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return;
                        date.setUTCDate(date.getUTCDate() - 1);
                        onChange(date.toISOString().split('T')[0]);
                    }}
                    className="flex items-center gap-1 p-1.5 rounded-lg hover:bg-white/10 text-text-muted hover:text-white transition-colors"
                >
                    <ChevronLeft size={16} />
                    <span className="text-[10px] uppercase font-bold tracking-wider">Previous Day</span>
                </button>

                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={clsx(
                        "flex-1 flex items-center justify-center gap-2 text-white/90 hover:bg-white/5 transition-colors rounded-lg py-1",
                        isOpen && "bg-white/5"
                    )}
                >
                    <Calendar size={14} className="text-red-400" />
                    <span className="text-sm font-medium">
                        {value ? formatDateIndian(value) : "Select Date"}
                    </span>
                </button>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!value) return;
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return;
                        date.setUTCDate(date.getUTCDate() + 1);
                        onChange(date.toISOString().split('T')[0]);
                    }}
                    className="flex items-center gap-1 p-1.5 rounded-lg hover:bg-white/10 text-text-muted hover:text-white transition-colors"
                >
                    <span className="text-[10px] uppercase font-bold tracking-wider">Next Day</span>
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Popup */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 z-50 min-w-[280px] p-4 rounded-2xl glass border border-white/10 shadow-2xl bg-[#0f172a]/95 backdrop-blur-xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <button
                                onClick={prevMonth}
                                className="p-1 rounded-lg hover:bg-white/10 text-text-muted hover:text-white transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm font-bold text-white tracking-wide">
                                {formatMonthYear(currentMonth)}
                            </span>
                            <button
                                onClick={nextMonth}
                                className="p-1 rounded-lg hover:bg-white/10 text-text-muted hover:text-white transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        {/* Week Days */}
                        <div className="grid grid-cols-7 mb-2">
                            {weekDays.map((day) => (
                                <div key={day} className="text-center text-[10px] font-bold text-text-muted uppercase tracking-wider">
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Days Grid */}
                        <div className="grid grid-cols-7 gap-1">
                            {days.map((dayObj, i) => {
                                const isSelectedDay = checkIsSelected(dayObj.date);
                                const isTodayDay = isToday(dayObj.date);

                                return (
                                    <button
                                        key={i}
                                        onClick={() => handleDateClick(dayObj.date)}
                                        className={clsx(
                                            "aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-all relative group",
                                            !dayObj.isCurrentMonth && "text-white/20",
                                            dayObj.isCurrentMonth && !isSelectedDay && "text-gray-300 hover:bg-white/10 hover:text-white",
                                            isSelectedDay && "bg-primary text-black shadow-lg shadow-primary/20 scale-105",
                                            isTodayDay && !isSelectedDay && "text-primary border border-primary/30"
                                        )}
                                    >
                                        <span className="relative z-10">{dayObj.date.getDate()}</span>
                                        {isTodayDay && !isSelectedDay && (
                                            <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary mx-auto"></div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
